import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FounderCard from "@/components/FounderCard";
import { ArrowUpRight, MessageCircle, Sparkles, LineChart, Users } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1643916861337-1e13c6f09b72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHdhcm0lMjBiZWlnZSUyMGdyYWRpZW50JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzg2MTk3OTl8MA&ixlib=rb-4.1.0&q=85";

const HERO_SPECIALISTS = [
  {
    name: "Myra",
    role: "HR",
    quote: "Great organizations grow when people grow.",
    avatar: "https://customer-assets.emergentagent.com/job_workday-partner/artifacts/fpzjeqcj_image.png",
  },
  {
    name: "Theo",
    role: "Marketing",
    quote: "Marketing works best when it feels like truth, not persuasion.",
    avatar: "https://images.unsplash.com/photo-1651684215020-f7a5b6610f23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxoZWFkc2hvdCUyMHBvcnRyYWl0JTIwc21pbGluZ3xlbnwwfHx8fDE3Nzg2MTk3OTJ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Grace",
    role: "Finance",
    quote: "Honest numbers lead to honest decisions.",
    avatar: "https://customer-assets.emergentagent.com/job_workday-partner/artifacts/829mbwsn_image.png",
  },
  {
    name: "Avi",
    role: "AI Tools",
    quote: "Most people use AI like a search engine. The real advantage comes when it becomes part of your workflow.",
    avatar: "https://customer-assets.emergentagent.com/job_workday-partner/artifacts/nkru0wvz_image.png",
  },
  {
    name: "Tyler",
    role: "Sales",
    quote: "The right decision usually becomes clear once the right questions are asked.",
    avatar: "https://customer-assets.emergentagent.com/job_workday-partner/artifacts/j4cm05vb_image.png",
  },
];

const FEATURES = [
  {
    icon: MessageCircle,
    title: "Talk through the day",
    body: "Decompress with a colleague who actually listens, asks the right follow-ups, and remembers what you shared.",
  },
  {
    icon: Sparkles,
    title: "Get advice that fits",
    body: "Tap HR, Marketing, Finance, Ops or Sales personas — or build your own — for grounded, practical guidance.",
  },
  {
    icon: Users,
    title: "Feedback on your work",
    body: "Paste a doc, deck note, or message draft and get structured feedback: strengths, concerns, next steps.",
  },
  {
    icon: LineChart,
    title: "Notice the pattern",
    body: "A quiet mood signal across your check-ins helps you see when work weeks are working — and when they aren't.",
  },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#FBF9F6]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "saturate(0.85)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[#FBF9F6]/75 backdrop-blur-[2px]"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-24 sm:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* LEFT: copy */}
            <div className="lg:col-span-7 anim-fade-up">
              <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-[#9A938A] mb-6">
                A colleague worth confiding in
              </p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-[#2D2926]">
                The coworker
                <br />
                who's also <span className="italic text-[#C29260]">a friend.</span>
              </h1>
              <p className="mt-8 text-lg sm:text-xl text-[#6B655D] leading-relaxed max-w-xl">
                Colleague is a persona you build to talk about the day at work, get advice on
                tough challenges, and get honest feedback on what you're working on.
              </p>
              <p className="mt-6 italic text-lg sm:text-xl text-[#6B655D] leading-relaxed max-w-xl">
                Professional when you need it. Personal when you don't.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link
                  to="/register"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-6 py-3 text-sm font-medium transition-colors"
                >
                  Build your Colleague
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/login"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 bg-[#F2EFE9] text-[#2D2926] hover:bg-[#E6E2D8] rounded-full px-6 py-3 text-sm font-medium transition-colors"
                >
                  Sign in
                </Link>
              </div>
            </div>

            {/* RIGHT: specialists stack */}
            <div
              className="lg:col-span-5 anim-fade-up anim-delay-2"
              data-testid="hero-specialists"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-[#9A938A] mb-5 lg:text-right">
                Specialists waiting inside
              </p>
              <ul className="space-y-3">
                {HERO_SPECIALISTS.map((s, i) => (
                  <li
                    key={s.name}
                    className="surface-card p-4 flex items-center gap-4 transition-all hover:-translate-y-0.5"
                    style={{ animationDelay: `${0.2 + i * 0.06}s` }}
                  >
                    <img
                      src={s.avatar}
                      alt={s.name}
                      className="w-12 h-12 rounded-full object-cover bg-[#F2EFE9] flex-shrink-0"
                      loading="lazy"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-serif text-lg text-[#2D2926]">{s.name}</span>
                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#C29260]">
                          {s.role}
                        </span>
                      </div>
                      <p className="text-sm text-[#6B655D] italic leading-snug line-clamp-2">
                        "{s.quote}"
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#9A938A] mt-4 lg:text-right">
                Or design your own — name, tone, expertise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="max-w-2xl mb-16">
          <p className="text-xs tracking-[0.25em] uppercase text-[#9A938A] mb-4">
            What lives inside
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight text-[#2D2926]">
            Not a chatbot. A coworker you can confide in.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                data-testid={`feature-${i}`}
                className={`surface-card p-8 anim-fade-up anim-delay-${i + 1} transition-all hover:-translate-y-1`}
              >
                <div className="w-11 h-11 rounded-full bg-[#F2EFE9] flex items-center justify-center mb-6">
                  <Icon className="text-[#C29260]" size={20} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl text-[#2D2926] mb-2">{f.title}</h3>
                <p className="text-[#6B655D] leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FOUNDER — after features so visitor sees the product first, then the person behind it */}
      <FounderCard />

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-32">
        <div className="surface-card p-12 sm:p-16 text-center bg-[#F2EFE9]">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#2D2926] tracking-tight mb-4">
            Meet your first Colleague.
          </h2>
          <p className="text-[#6B655D] max-w-xl mx-auto mb-8 leading-relaxed">
            Pick a workplace specialty or design one from scratch — name, tone, expertise,
            personality. They show up ready to talk.
          </p>
          <Link
            to="/register"
            data-testid="footer-cta"
            className="inline-flex items-center gap-2 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-7 py-3.5 text-sm font-medium transition-colors"
          >
            Get started — it's free
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#E6E2D8] py-8 text-center text-xs text-[#9A938A] tracking-wider uppercase">
        Colleague · An AI workplace companion
      </footer>
      <Footer />
    </div>
  );
}
